import { getCoreUtils, nz, StringOnlyOf } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppSearch, AppSearchChat, AppSearchData, AppSearchMessage } from "./AppSearch";
import { AppIndexReader } from "./AppIndex";

// AppSearchIndex - reads `search.json`, the message text the service publishes at the folder root
// for the main screen's search.
//
// ⚠ RULES ONLY - NO `window`, NO `XMLHttpRequest`: the Bridge suite may import this to pin the
// contract against the writer. The file is read through `AppSource.LoadPage`, like `index.json`.
//
// THE CONTRACT (schema 1):
//   { "schema": 1, "generator": "...", "perContactChars": 60000,
//     "contacts": [ { "href": "People/Seg/Seg.html", "title": "...", "personKey": "+46...",
//                     "clipped": true|false,
//                     "messages": [ ["2026-09-12T10:22", 1, "text", 1], ... ] } ] }   newest first
//   tuple = [UTC minute ISO (16 chars), fromMe 1|0, text, onPage 1|0, translation?]
//   ⭐ The optional 5th element (translation, 2026-09-27) is the message in the owner's language; a search matches
//   the original OR it, and the snippet shows whichever matched.
//
// ⛔ AN UNKNOWN SCHEMA IS REFUSED WITH A SENTENCE, never read with blanks - a search that silently
// finds nothing reads as "you never wrote that", which is the one wrong answer a search can give.

export class AppSearchIndex {
    public static readonly FileName: string = "search.json";
    public static readonly Schema: number = 1;

    public static Parse = (Text: string): AppSearchData => {
        if (getCoreUtils().KErrorMode === true) { return new AppSearchData(); }
        try {
            const Doc: Record<string, unknown> | null = AppIndexReader.ObjectIn(Text);
            if (Doc === null) { return AppSearchIndex.No(AppSearchIndex.UnreadableWhy); }
            if (Doc.schema !== AppSearchIndex.Schema) {
                return AppSearchIndex.No("Message search was prepared by a different version of WACommander"
                    + " (version " + String(Doc.schema) + "; this page reads " + String(AppSearchIndex.Schema)
                    + "). Reload the page to pick up the newest version.");
            }
            const Out: AppSearchData = new AppSearchData();
            Out.Loaded = true;
            if (Array.isArray(Doc.contacts) === false) { return Out; }
            for (const One of Doc.contacts as unknown[]) {
                if (One === null || typeof One !== "object") { continue; }
                Out.Chats.push(AppSearchIndex.ChatOf(One as Record<string, unknown>));
            }
            return Out;
        } catch (e) {
            // ERROR-SUPPRESSED-JUSTIFIED: a file this build cannot parse (truncated, a proxy's error
            // page) is an EXPECTED condition on a public wire - fate 1, said in a sentence.
            return AppSearchIndex.No(AppSearchIndex.UnreadableWhy);
        }
    };

    public static readonly UnreadableWhy: string = "Message search could not be read just now. WACommander makes it"
        + " again with each update, so try again in a few minutes.";

    /** One chat. ⚠ Malformed tuples are skipped, never read as blanks. */
    private static ChatOf = (Raw: Record<string, unknown>): AppSearchChat => {
        if (getCoreUtils().KErrorMode === true) { return new AppSearchChat(); }
        try {
            const Chat: AppSearchChat = new AppSearchChat();
            Chat.Href = StringOnlyOf(Raw.href);
            Chat.Title = StringOnlyOf(Raw.title);
            Chat.PersonKey = StringOnlyOf(Raw.personKey);
            Chat.ChatId = StringOnlyOf(Raw.chatId);
            Chat.Clipped = Raw.clipped === true;
            if (Chat.Title.length === 0) { Chat.Title = Chat.PersonKey; }
            if (Array.isArray(Raw.messages) === false) { return Chat; }
            for (const Tuple of Raw.messages as unknown[]) {
                const Msg: AppSearchMessage | null = AppSearchIndex.MessageOf(Tuple);
                if (Msg !== null) { Chat.Messages.push(Msg); }
            }
            return Chat;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchIndex.ChatOf");
            return new AppSearchChat();
        }
    };

    /** `[minute, fromMe, text, onPage]`, or null. ⭐ The text is folded HERE, once, not per keystroke. */
    public static MessageOf = (Tuple: unknown): AppSearchMessage | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Array.isArray(Tuple) === false) { return null; }
            const T: unknown[] = Tuple as unknown[];
            if (typeof T[0] !== "string" || typeof T[2] !== "string") { return null; }
            const Msg: AppSearchMessage = new AppSearchMessage();
            Msg.Minute = (T[0] as string).substring(0, 16);
            Msg.FromMe = T[1] === 1;
            Msg.Text = T[2] as string;
            Msg.OnPage = T[3] === 1;
            Msg.Folded = AppSearch.Fold(Msg.Text);
            if (typeof T[5] === "string") { Msg.MessageId = T[5] as string; }
            // ⭐ TRANSLATION: an optional 5th element carries the message in the owner's language.
            if (typeof T[4] === "string" && (T[4] as string).length > 0) {
                Msg.Translation = T[4] as string;
                Msg.FoldedTranslation = AppSearch.Fold(Msg.Translation);
            }
            return Msg;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchIndex.MessageOf");
            return null;
        }
    };

    /** DOCUMENTED EXCEPTION: builds a refusal from a sentence in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static No = (Why: string): AppSearchData => {
        const Out: AppSearchData = new AppSearchData();
        Out.Problem = nz(Why);
        return Out;
    };
}
